import type { SupabaseClient } from "@supabase/supabase-js";

import type {
  ProductFeedbackProblemKind,
  ProductFeedbackSystemInfo,
  ProductFeedbackType,
} from "./product-feedback";

export const PLATFORM_PRODUCT_FEEDBACK_LIST_RPC = "list_platform_product_feedback";
export const PLATFORM_PRODUCT_FEEDBACK_DETAIL_RPC = "get_platform_product_feedback";
export const PRODUCT_FEEDBACK_SCREENSHOT_SIGNER_FUNCTION =
  "product-feedback-screenshot-signer";
export const PRODUCT_FEEDBACK_SCREENSHOT_SIGNED_TTL_SECONDS = 60;

interface PlatformProductFeedbackSourceContext {
  readonly accountSourceDeleted: boolean;
  readonly submitterSourceDeleted: boolean;
  readonly sourceArchived: boolean;
}

export interface PlatformProductFeedbackSummary
  extends PlatformProductFeedbackSourceContext {
  readonly feedbackId: string;
  readonly createdAt: string;
  readonly accountNameSnapshot: string;
  readonly type: ProductFeedbackType;
  readonly preview: string;
  readonly appBuild: string | null;
  readonly hasScreenshot: boolean;
  readonly allowContactAtSubmission: boolean;
}

export interface PlatformProductFeedbackDetail
  extends PlatformProductFeedbackSourceContext {
  readonly feedbackId: string;
  readonly createdAt: string;
  readonly accountNameSnapshot: string;
  readonly reporterNameSnapshot: string;
  readonly reporterEmailSnapshot: string | null;
  readonly type: ProductFeedbackType;
  readonly problemKinds: readonly ProductFeedbackProblemKind[];
  readonly title: string | null;
  readonly message: string | null;
  readonly systemInfo: ProductFeedbackSystemInfo;
  readonly allowContactAtSubmission: boolean;
  readonly hasScreenshot: boolean;
  readonly screenshotMimeType: "image/png" | "image/jpeg" | null;
  readonly screenshotSizeBytes: number | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return asString(value) ?? undefined;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function asType(value: unknown): ProductFeedbackType | null {
  return value === "problem" || value === "idea" ? value : null;
}

function mapSourceContext(
  value: Record<string, unknown>,
): PlatformProductFeedbackSourceContext | null {
  const accountSourceDeleted = asBoolean(value.account_source_deleted);
  const submitterSourceDeleted = asBoolean(value.submitter_source_deleted);
  const sourceArchived = asBoolean(value.source_archived);
  if (
    accountSourceDeleted === null ||
    submitterSourceDeleted === null ||
    sourceArchived === null ||
    sourceArchived !== (accountSourceDeleted || submitterSourceDeleted)
  ) {
    return null;
  }
  return {
    accountSourceDeleted,
    submitterSourceDeleted,
    sourceArchived,
  };
}

function mapSystemInfo(value: unknown): ProductFeedbackSystemInfo {
  const record = asRecord(value);
  if (record === null) return {};
  const info: ProductFeedbackSystemInfo = {};
  for (const key of ["app_build", "browser_device", "page", "account_role", "session"] as const) {
    const item = asString(record[key]);
    if (item !== null) Object.assign(info, { [key]: item });
  }
  if (Array.isArray(record.error_codes)) {
    const errorCodes = record.error_codes
      .filter((item): item is string => typeof item === "string")
      .slice(-3);
    Object.assign(info, { error_codes: errorCodes });
  }
  return info;
}

export function mapPlatformProductFeedbackSummary(
  value: unknown,
): PlatformProductFeedbackSummary | null {
  const row = asRecord(value);
  if (row === null) return null;
  const feedbackId = asString(row.feedback_id);
  const createdAt = asString(row.created_at);
  const accountNameSnapshot = asString(row.account_name_snapshot);
  const type = asType(row.feedback_type);
  const preview = asString(row.preview);
  const sourceContext = mapSourceContext(row);
  const allowContactAtSubmission = asBoolean(row.allow_contact_at_submission);
  const hasScreenshot = asBoolean(row.has_screenshot);
  if (
    feedbackId === null ||
    createdAt === null ||
    accountNameSnapshot === null ||
    type === null ||
    preview === null ||
    sourceContext === null ||
    allowContactAtSubmission === null ||
    hasScreenshot === null
  ) {
    return null;
  }
  return {
    ...sourceContext,
    feedbackId,
    createdAt,
    accountNameSnapshot,
    type,
    preview,
    appBuild: asString(row.app_build),
    hasScreenshot,
    allowContactAtSubmission,
  };
}

export function mapPlatformProductFeedbackDetail(
  value: unknown,
): PlatformProductFeedbackDetail | null {
  const row = asRecord(value);
  if (row === null) return null;
  const feedbackId = asString(row.feedback_id);
  const createdAt = asString(row.created_at);
  const accountNameSnapshot = asString(row.account_name_snapshot);
  const reporterNameSnapshot = asString(row.reporter_name_snapshot);
  const reporterEmailSnapshot = asNullableString(row.reporter_email_snapshot);
  const type = asType(row.feedback_type);
  const sourceContext = mapSourceContext(row);
  const allowContactAtSubmission = asBoolean(row.allow_contact_at_submission);
  const hasScreenshot = asBoolean(row.has_screenshot);
  if (
    feedbackId === null ||
    createdAt === null ||
    accountNameSnapshot === null ||
    reporterNameSnapshot === null ||
    reporterEmailSnapshot === undefined ||
    type === null ||
    sourceContext === null ||
    allowContactAtSubmission === null ||
    hasScreenshot === null
  ) {
    return null;
  }
  const screenshotMimeType =
    row.screenshot_mime_type === "image/png" || row.screenshot_mime_type === "image/jpeg"
      ? row.screenshot_mime_type
      : null;
  const screenshotSizeBytes =
    typeof row.screenshot_size_bytes === "number" &&
    Number.isInteger(row.screenshot_size_bytes) &&
    row.screenshot_size_bytes > 0
      ? row.screenshot_size_bytes
      : null;
  const problemKinds = Array.isArray(row.problem_kinds)
    ? row.problem_kinds.filter(
        (item): item is ProductFeedbackProblemKind =>
          item === "broken" || item === "unexpected" || item === "slow" || item === "confusing",
      )
    : [];
  return {
    ...sourceContext,
    feedbackId,
    createdAt,
    accountNameSnapshot,
    reporterNameSnapshot,
    reporterEmailSnapshot,
    type,
    problemKinds,
    title: asString(row.title),
    message: asString(row.message),
    systemInfo: mapSystemInfo(row.system_info),
    allowContactAtSubmission,
    hasScreenshot,
    screenshotMimeType,
    screenshotSizeBytes,
  };
}

export async function fetchPlatformProductFeedback(
  client: SupabaseClient,
): Promise<PlatformProductFeedbackSummary[]> {
  const { data, error } = await client.rpc(PLATFORM_PRODUCT_FEEDBACK_LIST_RPC, {
    p_limit: 50,
  });
  if (error !== null) throw new Error("Could not load product feedback.");
  if (!Array.isArray(data)) return [];
  return data
    .map(mapPlatformProductFeedbackSummary)
    .filter((row): row is PlatformProductFeedbackSummary => row !== null);
}

export async function fetchPlatformProductFeedbackDetail(
  client: SupabaseClient,
  feedbackId: string,
): Promise<PlatformProductFeedbackDetail | null> {
  const { data, error } = await client.rpc(PLATFORM_PRODUCT_FEEDBACK_DETAIL_RPC, {
    p_feedback_id: feedbackId,
  });
  if (error !== null) throw new Error("Could not load this feedback report.");
  return Array.isArray(data) ? mapPlatformProductFeedbackDetail(data[0]) : null;
}

function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname.length > 0 &&
      url.username.length === 0 &&
      url.password.length === 0
    );
  } catch {
    return false;
  }
}

export async function requestProductFeedbackScreenshotSignedUrl(
  client: SupabaseClient,
  feedbackId: string,
): Promise<string> {
  try {
    const { data, error } = await client.functions.invoke(
      PRODUCT_FEEDBACK_SCREENSHOT_SIGNER_FUNCTION,
      { body: { feedbackId } },
    );
    const response = asRecord(data);
    const responseFeedbackId = response === null ? null : asString(response.feedbackId);
    const signedUrl = response === null ? null : asString(response.signedUrl);
    if (
      error !== null ||
      response === null ||
      response.kind !== "product-feedback-screenshot-signed-url" ||
      responseFeedbackId?.toLowerCase() !== feedbackId.toLowerCase() ||
      signedUrl === null ||
      !isHttpsUrl(signedUrl) ||
      response.expiresInSeconds !== PRODUCT_FEEDBACK_SCREENSHOT_SIGNED_TTL_SECONDS
    ) {
      throw new Error("Could not open this private screenshot.");
    }
    return signedUrl;
  } catch {
    throw new Error("Could not open this private screenshot.");
  }
}
